/** Right swipe (Erledigt) and a short left swipe (reveal) commit from 30 %
 * of the row width; a left swipe "ganz durch" from 70 % trashes directly. */
export const MAIL_SWIPE_COMMIT_RATIO = 0.3
export const MAIL_SWIPE_TRASH_RATIO = 0.7

export type MailSwipeAction = 'archive' | 'trash' | 'reveal'
/** Actions that move a conversation after the undo window. */
export type MailMoveAction = 'archive' | 'trash'
export type MailSnoozeAction = { kind: 'snooze'; until: Date; label: string }
export type MailDeferredAction = MailMoveAction | MailSnoozeAction

/** Axis selection is sticky: a scroll cannot turn into a destructive swipe. */
export class MailSwipe {
  private x = 0
  private y = 0
  private axis: 'waiting' | 'horizontal' | 'cancelled' = 'cancelled'
  delta = 0
  start(x: number, y: number) {
    this.x = x
    this.y = y
    this.delta = 0
    this.axis = 'waiting'
  }
  /** Returns the new horizontal offset, or null when nothing should move
   * (below the 12 px threshold, vertical scroll or cancelled gesture). */
  move(x: number, y: number): number | null {
    if (this.axis === 'cancelled') return null
    const dx = x - this.x
    const dy = y - this.y
    if (this.axis === 'waiting') {
      if (Math.max(Math.abs(dx), Math.abs(dy)) < 12) return null
      if (Math.abs(dy) >= Math.abs(dx)) {
        this.cancel()
        return null
      }
      this.axis = 'horizontal'
    }
    this.delta = dx
    return dx
  }
  end(width: number): MailSwipeAction | null {
    const distance = Math.abs(this.delta)
    let action: MailSwipeAction | null = null
    if (this.axis === 'horizontal' && width > 0 && distance >= width * MAIL_SWIPE_COMMIT_RATIO) {
      if (this.delta > 0) action = 'archive'
      else action = distance >= width * MAIL_SWIPE_TRASH_RATIO ? 'trash' : 'reveal'
    }
    this.cancel()
    return action
  }
  cancel() {
    this.axis = 'cancelled'
    this.delta = 0
  }
}

/** No IMAP request exists until the complete undo window has elapsed. */
export class DeferredMailAction {
  private timer: ReturnType<typeof setTimeout>
  private pending = true
  constructor(run: () => void) {
    this.timer = setTimeout(() => {
      this.pending = false
      run()
    }, 5000)
  }
  undo() {
    if (!this.pending) return false
    this.pending = false
    clearTimeout(this.timer)
    return true
  }
}

export interface MailActionClient {
  archiveMailMessage(accountId: string, messageId: string): Promise<unknown>
  trashMailMessage(accountId: string, messageId: string): Promise<unknown>
  snoozeMailConversation(accountId: string, messageIds: string[], until: string): Promise<unknown>
}

/** The network half of a deferred action; runs only once the undo window closed.
 * A snooze is one request for the whole conversation so it forms one group. */
export async function performMailAction(
  client: MailActionClient,
  accountId: string,
  messageIds: string[],
  action: MailDeferredAction,
) {
  if (typeof action === 'object') {
    await client.snoozeMailConversation(accountId, messageIds, action.until.toISOString())
    return
  }
  for (const id of messageIds) {
    if (action === 'archive') await client.archiveMailMessage(accountId, id)
    else await client.trashMailMessage(accountId, id)
  }
}

/** Snooze errors never echo server text (it may carry IMAP details). */
export function mailActionErrorMessage(action: MailDeferredAction, error: unknown): string {
  if (typeof action !== 'object') {
    return error instanceof Error ? error.message : 'Verschieben fehlgeschlagen'
  }
  const { status, code } = (error ?? {}) as { status?: number; code?: string }
  if (status === 409 && code === 'protected_folder') {
    return 'Geschützter Ordner: „Später“ nicht möglich'
  }
  if (status === 400) return 'Später nicht möglich: Zeitpunkt ungültig'
  return 'Später fehlgeschlagen'
}
